// Shared user-directory resolution (docs/09 §6): people render via the
// `GET /v1/users?ids=` join — never a per-row fetch, never an email guess. An
// unresolved id degrades to a short mono id. InvitationsSettings and the
// memberships views share one module instead of importing helpers from a
// sibling screen.

import { useQuery } from "@tanstack/react-query";

import type { components, operations } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError } from "../../lib/errors";
import { DEFAULT_PAGE_SIZE } from "../../lib/pagination";
import { queryKeys } from "../../lib/queryKeys";
import { Copyable, Stack, Text } from "../../ui";

type User = components["schemas"]["UserResponse"];
type UserListQuery = NonNullable<
  operations["usersList"]["parameters"]["query"]
>;

export function userMap(users: User[]): ReadonlyMap<string, User> {
  return new Map(users.map((user) => [user.id, user]));
}

export function uniqueIds(ids: Iterable<string | null | undefined>): string[] {
  return Array.from(
    new Set(
      Array.from(ids)
        .map((id) => id?.trim() ?? "")
        .filter((id) => id.length > 0),
    ),
  ).sort();
}

export function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

export function usersListQuery(
  pageToken: string | undefined,
  ids?: string,
): UserListQuery {
  return ids
    ? {
        ids,
        page_size: DEFAULT_PAGE_SIZE,
        page_token: pageToken,
      }
    : { page_size: DEFAULT_PAGE_SIZE, page_token: pageToken };
}

// Directory joins are keyed by the full id set + calling surface. Network
// requests are bounded to one default-size page each so the server-side ids
// cap and page size cannot leave later loaded rows unresolved.
export function useUserDirectoryQuery(idsParam: string, surface: string) {
  return useQuery({
    enabled: idsParam.length > 0,
    queryKey: queryKeys.users.list({ ids: idsParam, surface }),
    queryFn: async () => {
      const ids = idsParam.split(",").filter((id) => id.length > 0);
      const chunks = Array.from(
        { length: Math.ceil(ids.length / DEFAULT_PAGE_SIZE) },
        (_, index) =>
          ids.slice(index * DEFAULT_PAGE_SIZE, (index + 1) * DEFAULT_PAGE_SIZE),
      );
      const pages = await Promise.all(
        chunks.map(async (chunk) => {
          const { data, error } = await apiClient.GET("/v1/users", {
            params: {
              query: usersListQuery(undefined, chunk.join(",")),
            },
          });
          if (error) {
            throw new ApiRequestError(error);
          }
          return data;
        }),
      );
      return {
        items: pages.flatMap((page) => page.items),
        next_page_token: null,
      };
    },
  });
}

export function userDisplayName(user: User): string {
  const displayName = user.display_name?.trim();
  return displayName && displayName.length > 0 ? displayName : user.email;
}

// "Ada Lovelace (ada@example.com)", or just the email when no display name.
export function userOptionLabel(user: User): string {
  const label = userDisplayName(user);
  return label === user.email ? label : `${label} (${user.email})`;
}

export function UserLabel({
  compact = false,
  fallbackId,
  user,
}: {
  compact?: boolean;
  fallbackId: string;
  user?: User;
}) {
  if (!user) {
    return <Copyable label="User ID" value={fallbackId} />;
  }

  if (compact) {
    return (
      <Text as="span" title={userOptionLabel(user)} truncate>
        {userDisplayName(user)}
      </Text>
    );
  }

  return (
    <Stack gap={1}>
      <Text as="span">{userDisplayName(user)}</Text>
      <Text as="span" variant="muted">
        {user.email}
      </Text>
    </Stack>
  );
}
